import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Text } from '~/components/typography'
import { useAppStore } from '~/hooks'
import CloseIcon from '~/assets/icons/close.svg?react'
import { cn } from '~/util'
import { MapTooltipMachineContext } from '~/contexts/MapTooltipMachineContext'
import { CALWAVE_PLOTS_S3_PREFIX, originUrl } from '~/cdnAssets'
import { GRAPHS } from '~/constants'

const BASE_URL = originUrl(CALWAVE_PLOTS_S3_PREFIX)

function OverlayPanel() {
  const { basemapType, setOverlayPanelVisible, selectedGraphType, dataset } = useAppStore()
  const { context, value } = MapTooltipMachineContext.useSelector((s) => s)
  const [iframeContentLoaded, setIframeContentLoaded] = useState(false)

  // Only use point name when it's from a click, not from hover
  const pointName = value === 'visible_from_click' ? context.wavePointName : null

  const selectedGraph = selectedGraphType ? GRAPHS[selectedGraphType] : undefined

  const graphUrl = (() => {
    if (!selectedGraphType || !pointName || dataset !== 'waves') return null
    const graph = GRAPHS[selectedGraphType]
    if (!graph) return null
    return `${BASE_URL}/${pointName}/${pointName}${graph.htmlSuffix}`
  })()

  const { isPending: isGraphUrlChecking, isError: isGraphUrlFailed } = useQuery({
    queryKey: ['graphUrlCheck', graphUrl],
    queryFn: async () => {
      if (!graphUrl) throw new Error('No graph URL')
      const res = await fetch(graphUrl, { method: 'HEAD' })
      if (!res.ok) throw new Error(`Graph URL returned ${res.status}`)
      return true
    },
    enabled: !!graphUrl,
    retry: false,
  })

  // Reset iframe loaded state when switching graphs or points
  useEffect(() => {
    setIframeContentLoaded(false)
  }, [graphUrl])

  if (!graphUrl) {
    return null
  }

  return (
    <div
      className={cn(
        'mr-14 flex w-full max-w-[600px] flex-col rounded-xl bg-neutral-off-white text-start sm:w-[600px]',
        basemapType === 'FLAT' ? 'shadow-xl' : ''
      )}
    >
      <div className="flex items-center justify-between px-4 pt-2">
        {selectedGraph && (
          <Text level={1} className="text-neutral-midfi-grey">
            {selectedGraph.label}
            {pointName ? ` — ${pointName}` : ''}
          </Text>
        )}
        <button
          onClick={() => setOverlayPanelVisible(false)}
          className="text-neutral-midfi-grey transition-colors hover:text-neutral-dark-grey"
          aria-label="Close graph panel"
        >
          <CloseIcon width={20} height={20} />
        </button>
      </div>
      <div className="relative p-4">
        <div className="flex min-h-[300px] flex-col gap-2 sm:max-h-[70vh] sm:min-h-[400px]">
          {isGraphUrlFailed ? (
            <div
              className="flex flex-col items-center justify-center gap-2 rounded-lg border border-neutral-midfi-grey/30 bg-neutral-midfi-grey/5 p-6 text-center"
              role="alert"
            >
              <Text level={3} className="text-neutral-midfi-grey">
                Interactive graph could not be loaded. Please try again or refresh.
              </Text>
            </div>
          ) : isGraphUrlChecking ? (
            <div
              className="flex min-h-[300px] flex-col items-center justify-center gap-2 rounded-lg border border-neutral-midfi-grey/30 bg-neutral-midfi-grey/5 p-6"
              aria-busy="true"
              aria-live="polite"
            >
              <div
                className="h-10 w-10 animate-spin rounded-full border-2 border-neutral-midfi-grey border-t-transparent"
                aria-hidden
              />
              <Text level={3} className="text-neutral-midfi-grey">
                Loading graph…
              </Text>
            </div>
          ) : (
            <div
              className={cn(
                'relative max-h-[85vh] min-h-[300px] w-full',
                selectedGraph?.heightClass ?? 'h-[400px] sm:h-[500px]'
              )}
            >
              <iframe
                key={graphUrl}
                src={graphUrl}
                title={selectedGraph?.label ?? 'Interactive graph'}
                className="h-full w-full rounded-lg"
                sandbox="allow-scripts allow-same-origin"
                onLoad={() => setIframeContentLoaded(true)}
              />
              {!iframeContentLoaded && (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-lg bg-neutral-off-white"
                  aria-busy="true"
                  aria-live="polite"
                >
                  <div
                    className="h-10 w-10 animate-spin rounded-full border-2 border-neutral-midfi-grey border-t-transparent"
                    aria-hidden
                  />
                  <Text level={3} className="text-neutral-midfi-grey">
                    Loading graph…
                  </Text>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default OverlayPanel
